import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useWarehousePermissions } from "@/hooks/useWarehousePermissions";
import ThemeToggle from "@/components/ThemeToggle";
import Logo from "@/components/Logo";
import {
  LayoutDashboard, PackageSearch, ScanLine, PackageOpen, Layers,
  Truck, FilePlus2, PackageCheck, Send, Users, History, AlertTriangle,
  Settings as SettingsIcon, LogOut, Menu, X, Warehouse as WarehouseIcon,
  Package as PackageIcon,
} from "lucide-react";

const NAV = [
  { label: "Overview", items: [
    { to: "/warehouse", end: true, icon: LayoutDashboard, label: "Dashboard", show: () => true },
  ]},
  { label: "Intake", items: [
    { to: "/warehouse/incoming", icon: PackageSearch, label: "Incoming Packages", show: (p) => p.can("view_incoming") },
    { to: "/warehouse/scan", icon: ScanLine, label: "Scan Package Intake", show: (p) => p.can("scan_intake") },
    { to: "/warehouse/received", icon: PackageOpen, label: "Received Packages", show: (p) => p.can("view_received") },
    { to: "/warehouse/sorting", icon: Layers, label: "Package Sorting", show: (p) => p.canAny("mark_sorting", "add_to_batch") },
  ]},
  { label: "Outbound", items: [
    { to: "/warehouse/batches", icon: Truck, label: "Outbound Batches", show: (p) => p.can("view_batches") },
    { to: "/warehouse/batches/new", icon: FilePlus2, label: "Create Outbound Batch", show: (p) => p.can("create_batch") },
    { to: "/warehouse/shipped", icon: Send, label: "Shipped Out Packages", show: (p) => p.can("view_batches") },
    { to: "/warehouse/ready-last-mile", icon: PackageCheck, label: "Ready for Last-Mile", show: (p) => p.can("view_batches") },
  ]},
  { label: "Team", items: [
    { to: "/warehouse/dispatchers", icon: Users, label: "Warehouse Dispatchers", show: (p) => p.isAdmin },
  ]},
  { label: "Tracking", items: [
    { to: "/warehouse/history", icon: History, label: "Warehouse History", show: (p) => p.can("view_history") },
    { to: "/warehouse/issues", icon: AlertTriangle, label: "Issues", show: (p) => p.canAny("report_issue", "view_history") },
  ]},
  { label: "System", items: [
    { to: "/warehouse/settings", icon: SettingsIcon, label: "Settings", show: () => true },
  ]},
];

export default function WarehouseLayout() {
  const { user, logout } = useAuth();
  const perms = useWarehousePermissions();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const groups = NAV
    .map(g => ({ ...g, items: g.items.filter(i => i.show(perms)) }))
    .filter(g => g.items.length > 0);

  const roleLabel = perms.isAdmin ? "Warehouse Admin" : "Warehouse Dispatcher";

  const NavBody = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-3 min-w-0">
          <Logo className="h-11 shrink-0" />
          <div className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-cyan-400 truncate border-l border-slate-200 dark:border-slate-800 pl-3">
            {roleLabel}
          </div>
        </div>
        <button
          data-testid="wh-sidebar-close"
          onClick={() => setMobileOpen(false)}
          className="md:hidden text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:dark:text-white"
        >
          <X className="w-5 h-5" />
        </button>
        <ThemeToggle size="sm" className="hidden md:grid" />
      </div>
      <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4 space-y-6">
        {groups.map(group => (
          <div key={group.label}>
            <div className="px-3 mb-2 text-[10px] font-mono-tech uppercase tracking-[0.2em] text-slate-500">
              {group.label}
            </div>
            <ul className="space-y-1">
              {group.items.map(item => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`wh-sidebar-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={({ isActive }) =>
                      [
                        "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                        isActive
                          ? "bg-cyan-500/10 text-cyan-300 border border-cyan-500/30"
                          : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 hover:dark:bg-slate-900 border border-transparent",
                      ].join(" ")
                    }
                  >
                    <item.icon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="border-t border-slate-200 dark:border-slate-800 p-4">
        <div className="px-3 py-2 mb-2 rounded-md bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
          <div className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500">Signed in</div>
          <div className="text-sm font-display font-semibold truncate">{user?.full_name || user?.name}</div>
          <div className="text-[11px] text-slate-600 dark:text-slate-400 truncate">{user?.email}</div>
        </div>
        <button
          data-testid="wh-logout-btn"
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-red-400 hover:bg-red-500/10"
        >
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white flex">
      <aside className="hidden md:flex w-64 shrink-0 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
        <NavBody />
      </aside>
      <div className="flex-1 flex flex-col">
        <header className="md:hidden sticky top-0 z-30 glass border-b border-slate-200 dark:border-slate-800 px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-cyan-400 text-slate-950 grid place-items-center">
              <PackageIcon className="w-4 h-4" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-sm">Warehouse</span>
          </div>
          <button data-testid="wh-sidebar-open" onClick={() => setMobileOpen(true)}>
            <Menu className="w-5 h-5" />
          </button>
        </header>
        <main className="flex-1 px-5 sm:px-8 py-6 max-w-7xl mx-auto w-full">
          <Outlet />
        </main>
      </div>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800">
            <NavBody />
          </aside>
        </div>
      )}
    </div>
  );
}
